/**
 * RunRuntimeRow (NC-1294 Slice 1, FE half)
 *
 * Shows how long a run has been going ("Läuft seit 2 h 13 min") or how long it
 * ran ("Lief 2 h 13 min"). The classification of the run's lifecycle is done
 * server-side by api/shared/runRuntime.ts — this component renders that verdict
 * and never re-derives it, so there is one definition of "when did it start".
 *
 * Two honesty constraints baked in:
 *
 *  - A live value is ticked ONLY when the API says `isLive`. For an ended run the
 *    server's `elapsedMs` is authoritative and no timer is armed.
 *  - The number is WALL-CLOCK, including any stopped stretch in between. The
 *    issue also asks for accumulated VM hours across start/stop cycles; the API
 *    does not compute that yet (`source` is always 'wall-clock'), so the label
 *    and tooltip say wall-clock instead of implying billed VM time.
 */

import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { RunRuntime } from '../services/types';
import { formatDuration } from '../utils/formatDuration';
import { useLiveElapsed } from '../hooks/useLiveElapsed';
import { formatDateSafe } from '../utils/dateFormat';

interface RunRuntimeRowProps {
  runtime?: RunRuntime;
}

const WALL_CLOCK_NOTE =
  'Wall-Clock zwischen Start und Ende des Laufs. Zwischenzeitliche Stopps sind mitgezaehlt — akkumulierte VM-Stunden ueber mehrere Start/Stop-Zyklen berechnet die API noch nicht.';

export const RunRuntimeRow: React.FC<RunRuntimeRowProps> = ({ runtime }) => {
  // The hook is called unconditionally (rules of hooks) but arms no timer unless
  // the API says the run is live.
  const liveElapsed = useLiveElapsed(runtime?.startedAtUtc, !!runtime?.isLive);

  // Legacy/sparse doc with no usable anchor: the API says stay neutral, so
  // render nothing rather than inventing a state.
  if (!runtime || runtime.state === 'unknown') return null;

  if (runtime.state === 'not-started') {
    return (
      <Box data-testid="run-runtime-not-started">
        <Typography variant="body2" color="text.secondary">
          Noch nicht gestartet
          {runtime.startedAtUtc ? ` — geplant fuer ${formatDateSafe(runtime.startedAtUtc)}` : ''}
        </Typography>
      </Box>
    );
  }

  const elapsedMs = runtime.state === 'running' ? liveElapsed : runtime.elapsedMs;
  const formatted = formatDuration(elapsedMs);

  // Ended, but the end timestamp was never written (source 'none'). Say that
  // instead of showing a duration we cannot stand behind.
  if (formatted === null) {
    return (
      <Box data-testid="run-runtime-unmeasurable">
        <Typography variant="body2" color="text.secondary">
          {runtime.state === 'running' ? 'Laeuft' : 'Beendet'} — Dauer nicht ermittelbar
        </Typography>
      </Box>
    );
  }

  const isRunning = runtime.state === 'running';

  return (
    <Tooltip title={WALL_CLOCK_NOTE}>
      <Box data-testid={`run-runtime-${isRunning ? 'running' : 'ended'}`}>
        <Typography variant="body2">
          {isRunning ? `Laeuft seit ${formatted}` : `Lief ${formatted}`}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default RunRuntimeRow;
